import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IdCard, Ticket, Globe, Smartphone, ShieldCheck, RefreshCw, QrCode,
    ArrowRight, LogIn, MessageCircle, Image as ImageIcon, Check, Sparkles, Download, UserPlus,
} from 'lucide-react';
import Cookies from 'js-cookie';
import { whatsappUrl } from '../../config';
import Footer from '../../components/Footer';
import logo_iti from '../../assets/logo_iti.png';
import meiaentrada from '../../assets/meiaentrada.png';
import etapa1 from '../../assets/etapa1.png';
import etapa2 from '../../assets/etapa2.png';
import etapa3 from '../../assets/etapa3.png';
import carteiradigitalex from '../../assets/carteiradigitalex.png';
import digital from '../../assets/imagemcarteiraexemplo.png';
import './landing.css';

/**
 * Imagem da landing com placeholder elegante.
 * Enquanto o arquivo não existir em /public/landing/, mostra uma moldura
 * tracejada com o nome esperado do arquivo. Basta soltar a imagem lá.
 */
function LandingImage({ src, alt, label, className = '' }) {
    const [error, setError] = useState(false);
    if (error) {
        return (
            <div className={`lp-imgph ${className}`}>
                <ImageIcon size={30} />
                <span>{label || alt}</span>
                <small>{src.split('/').pop()}</small>
            </div>
        );
    }
    return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
}

const BENEFICIOS = [
    { ic: Ticket, color: '#e2e144', label: 'Meia-entrada nacional', text: 'Pague metade em cinemas, shows, teatros e eventos esportivos por todo o Brasil.' },
    { ic: Globe, color: '#5fe39a', label: 'Validade em todo o país', text: 'Documento reconhecido nacionalmente, dentro da lei da meia-entrada.' },
    { ic: Smartphone, color: '#7cc4ff', label: '100% digital', text: 'Sua carteirinha sempre com você no celular, pronta para apresentar.' },
    { ic: ShieldCheck, color: '#c5a3ff', label: 'Padrão oficial ITI', text: 'Assinatura e validação digital seguindo o padrão nacional de certificação.' },
    { ic: RefreshCw, color: '#ffb27c', label: 'Renovação simples', text: 'Renove a cada ano de forma rápida, sem burocracia e sem perder benefícios.' },
    { ic: QrCode, color: '#ff8a86', label: 'QR de validação', text: 'Qualquer estabelecimento confirma a autenticidade em segundos pelo QR Code.' },
];

const PASSOS = [
    { n: '1', img: etapa1, title: 'Faça seu cadastro', text: 'Garanta sua carteirinha com a UNAE e tenha seus dados de estudante registrados.' },
    { n: '2', img: etapa2, title: 'Receba seu acesso', text: 'Você recebe o acesso por e-mail para entrar no portal com e-mail e senha.' },
    { n: '3', img: etapa3, title: 'Baixe e use', text: 'Acesse, baixe a versão digital e apresente onde a meia-entrada for aceita.' },
];

export default function Landing() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const logado = Cookies.get('usuario') && Cookies.get('cpf') && Cookies.get('user_id');

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const irParaLogin = () => navigate(logado ? '/visualizaminhacarteira' : '/login');

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    const falarComAtendente = () => {
        window.open(whatsappUrl(), '_blank');
    };

    return (
        <div className="lp">
            {/* ===== NAV ===== */}
            <nav className={`lp-nav ${scrolled ? 'is-solid' : ''}`}>
                <div className="lp-wrap lp-nav__inner">
                    <div className="lp-brand">
                        <img src={`${process.env.PUBLIC_URL}/nomeUnae.png`} alt="UNAE Estudantil" className="lp-brand__img" />
                    </div>
                    <div className="lp-nav__links">
                        <button className="lp-nav__link" onClick={() => scrollTo('beneficios')}>Benefícios</button>
                        <button className="lp-nav__link" onClick={() => scrollTo('como')}>Como funciona</button>
                        {!logado && (
                            <button
                                className="lp-nav__ghost"
                                onClick={falarComAtendente}
                                title="Fale com o atendente para criar sua conta"
                            >
                                <UserPlus size={16} /> Criar conta
                            </button>
                        )}
                        <button className="lp-nav__cta" onClick={irParaLogin}>
                            <LogIn size={16} /> {logado ? 'Minha carteira' : 'Entrar'}
                        </button>
                    </div>
                </div>
            </nav>

            {/* ===== HERO ===== */}
            <header className="lp-hero">
                <div className="lp-wrap lp-hero__grid">
                    <div className="lp-hero__intro">
                        <div className="lp-eyebrow"><IdCard size={15} /> Carteira Estudantil Digital</div>
                        <h1 className="lp-hero__title">
                            Sua carteira de estudante, agora <span>100% digital</span>.
                        </h1>
                        <p className="lp-hero__lead">
                            Meia-entrada garantida em cinema, shows, teatro e eventos esportivos por todo o país.
                            Validade nacional, segurança oficial e acesso na palma da mão.
                        </p>
                        <div className="lp-hero__actions">
                            <button className="lp-btn lp-btn--gold" onClick={irParaLogin}>
                                {logado ? 'Acessar minha carteira' : 'Acessar minha carteira'} <ArrowRight size={18} />
                            </button>
                            <button className="lp-btn lp-btn--glass" onClick={() => scrollTo('beneficios')}>
                                Ver benefícios
                            </button>
                        </div>

                        <div className="lp-hero__trust">
                            <img src={logo_iti} alt="ITI" /> 
                            <span>Validação oficial ITI · Lei da meia-entrada</span>
                        </div>
                    </div>

                    {/* palco do card */}
                    <div className="lp-hero__stage">
                        <div className="lp-cardframe">
                            <LandingImage
                                src={digital}
                                alt="Carteirinha estudantil digital UNAE"
                                label="Carteirinha digital"
                                className="lp-cardframe__img"
                            />
                            <div className="lp-cardframe__shine" />
                        </div>
                    </div>
                </div>
            </header>

            {/* ===== 01 · BENEFÍCIOS ===== */}
            <section id="beneficios" className="lp-band">
                <div className="lp-wrap">
                    <div className="lp-shead">
                        <div className="lp-shead__left">
                            <span className="lp-shead__num">01</span>
                            <div>
                                <div className="lp-shead__eyebrow">Vantagens</div>
                                <h2 className="lp-shead__title">Tudo que sua carteirinha oferece</h2>
                            </div>
                        </div>
                        <p className="lp-shead__desc">Um único documento que abre as portas dos benefícios estudantis em todo o Brasil.</p>
                    </div>

                    <div className="lp-features">
                        {BENEFICIOS.map((b, i) => {
                            const Ic = b.ic;
                            return (
                                <div className="lp-feature" key={i}>
                                    <div className="lp-feature__ic" style={{ background: `${b.color}1f`, color: b.color }}>
                                        <Ic size={24} />
                                    </div>
                                    <div className="lp-feature__label">{b.label}</div>
                                    <div className="lp-feature__text">{b.text}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== 02 · COMO FUNCIONA ===== */}
            <section id="como" className="lp-band lp-band--alt">
                <div className="lp-wrap">
                    <div className="lp-shead">
                        <div className="lp-shead__left">
                            <span className="lp-shead__num">02</span>
                            <div>
                                <div className="lp-shead__eyebrow">Passo a passo</div>
                                <h2 className="lp-shead__title">Como ter a sua</h2>
                            </div>
                        </div>
                        <p className="lp-shead__desc">Em poucos passos sua carteira estudantil fica pronta para uso.</p>
                    </div>

                    <div className="lp-steps">
                        {PASSOS.map((p) => (
                            <div className="lp-step" key={p.n}>
                                <div className="lp-step__media">
                                    <LandingImage
                                        src={p.img}
                                        alt={p.title}
                                        label={`Passo ${p.n}`}
                                        className="lp-step__img"
                                    />
                                    <span className="lp-step__num">{p.n}</span>
                                </div>
                                <h3 className="lp-step__title">{p.title}</h3>
                                <p className="lp-step__text">{p.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== 03 · SHOWCASE (digital + física) ===== */}
            <section className="lp-band">
                <div className="lp-wrap lp-showcase">
                    <div className="lp-showcase__media">
                        <LandingImage
                            src={carteiradigitalex}
                            alt="Carteirinha estudantil UNAE"
                            label="Destaque da carteirinha"
                            className="lp-showcase__img"
                        />
                    </div>
                    <div className="lp-showcase__text">
                        <div className="lp-eyebrow lp-eyebrow--dark"><Sparkles size={15} /> Identidade estudantil</div>
                        <h2 className="lp-showcase__title">Moderna, segura e sempre com você</h2>
                        <p className="lp-showcase__lead">
                            A carteira digital da UNAE traz seus dados de estudante com QR Code de validação,
                            pronta para ser apresentada direto do celular — sem precisar imprimir nada.
                        </p>
                        <ul className="lp-checklist">
                            <li><Check size={17} /> Foto, curso, instituição e validade em um só lugar</li>
                            <li><Check size={17} /> Download em PDF para guardar ou imprimir</li>
                            <li><Check size={17} /> Comprovante de validação com assinatura digital</li>
                            <li><Check size={17} /> Acesso rápido a qualquer momento pelo portal</li>
                        </ul>
                        <button className="lp-btn lp-btn--dark" onClick={irParaLogin}>
                            <Download size={18} /> Acessar minha carteira
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== CTA FINAL ===== */}
            <section className="lp-cta">
                <div className="lp-wrap lp-cta__inner">
                    <div className="lp-cta__seal">
                        <img src={`${process.env.PUBLIC_URL}/unaeBrasao.png`} alt="Brasão UNAE" />
                    </div>
                    <div className="lp-eyebrow"><IdCard size={15} /> Portal do estudante</div>
                    <h2 className="lp-cta__title">Já tem sua carteirinha UNAE?</h2>
                    <p className="lp-cta__text">Entre com seu e-mail e senha para visualizar, baixar e validar a sua carteira estudantil.</p>
                    <div className="lp-cta__actions">
                        <button className="lp-btn lp-btn--gold" onClick={irParaLogin}>
                            <LogIn size={18} /> {logado ? 'Ir para minha carteira' : 'Entrar agora'}
                        </button>
                        <button className="lp-btn lp-btn--glass" onClick={falarComAtendente}>
                            <MessageCircle size={18} /> Falar com atendente
                        </button>
                    </div>
                    <p className="lp-cta__hint">Não é possível criar conta por aqui. Fale com o atendente para liberar seu acesso.</p>
                </div>
            </section>

            <Footer />
        </div>
    );
}
